import { ScoreDefinitionDTO } from 'business/models/Models';
import { Plate, ScoresOfWellDrops } from 'components/Experiment/constants';
import { GetScoreDefn } from 'utils/Global';
import { ScoreGroupType } from '../Contants';
import { FilterWell, getWellKey } from '../utils';
import { HitScoreName } from './constants';

function getScoreDefinitionsById(): Map<number, ScoreDefinitionDTO> {
  return new Map(
    GetScoreDefn().map((definition) => [definition.wellDropScoreId, definition]),
  );
}

function getSelectedInspection(
  inspections: any[] | undefined,
  inspectionId: number | undefined,
) {
  return inspections?.find((insp) => insp.id === inspectionId);
}

function getScoreDefinition(
  score: ScoresOfWellDrops,
  definitions: Map<number, ScoreDefinitionDTO>,
): ScoreDefinitionDTO | undefined {
  return definitions.get(score.wellDropScoreId);
}

function isScoreInScope(
  score: ScoresOfWellDrops,
  scoreGroupType: ScoreGroupType,
): boolean {
  if (scoreGroupType === ScoreGroupType.Auto) {
    return score.isAutoScore === true;
  }
  if (scoreGroupType === ScoreGroupType.Manual) {
    return score.isAutoScore !== true;
  }
  return true;
}

export function isUvPositive(score: ScoresOfWellDrops): boolean {
  return score.scoreName === HitScoreName.UvPositive;
}

function isPositiveHitScore(
  score: ScoresOfWellDrops,
  definitions: Map<number, ScoreDefinitionDTO>,
  scoreGroupType: ScoreGroupType,
): boolean {
  if (isUvPositive(score)) {
    return true;
  }

  const definition = getScoreDefinition(score, definitions);
  if (!definition) {
    return false;
  }

  if (scoreGroupType === ScoreGroupType.Manual) {
    return definition.hit === true;
  }

  return definition.hit === true || definition.scoreName === HitScoreName.PositiveHit;
}

export function getAllPlateScores(
  plateDto: Plate[] | undefined,
  selectedInspection: ReturnType<typeof getSelectedInspection>,
  dropNumber: number | undefined,
): ScoresOfWellDrops[] {
  const allScores: ScoresOfWellDrops[] = [];

  for (const plate of plateDto ?? []) {
    const plateGroupedInsp = selectedInspection?.groupedInspections?.find(
      (grp) => grp.plateId === plate.plateId,
    );

    if (!plateGroupedInsp || !plate.plateScores) {
      continue;
    }

    for (const batchScore of plate.plateScores.flat()) {
      if (batchScore.dropNumber != dropNumber) {
        continue;
      }
      if (batchScore.scoreBatchId !== plateGroupedInsp.imageBatchId) {
        continue;
      }

      allScores.push({
        ...batchScore,
        plateId: plate.plateId,
      });
    }
  }

  return allScores;
}

export function attachScoreDefinitions(scores: ScoresOfWellDrops[]) {
  const definitions = getScoreDefinitionsById();

  return scores.map((score) => ({
    ...score,
    scoreDefinition: definitions.get(score.wellDropScoreId),
  }));
}

export function filterScoresBySelectedGroups(
  scores: ScoresOfWellDrops[],
  scoreGroupType: ScoreGroupType,
  selectedHitScoreGroups: number[],
  definitions = getScoreDefinitionsById(),
): ScoresOfWellDrops[] {
  if (selectedHitScoreGroups.length === 0) {
    return [];
  }

  const selectedGroupSet = new Set(selectedHitScoreGroups.map(Number));

  return scores.filter(
    (score) =>
      selectedGroupSet.has(Number(score.wellDropScoreGroupId)) ||
      (scoreGroupType !== ScoreGroupType.Manual && isUvPositive(score)),
  );
}

export function filterScoresByPositiveHits(
  scores: ScoresOfWellDrops[],
  scoreGroupType: ScoreGroupType,
  definitions = getScoreDefinitionsById(),
): ScoresOfWellDrops[] {
  return scores.filter(
    (score) =>
      isScoreInScope(score, scoreGroupType) &&
      isPositiveHitScore(score, definitions, scoreGroupType),
  );
}

function getGroupedScores(
  inspections: any[] | undefined,
  plateDto: Plate[] | undefined,
  inspectionId: number | undefined,
  dropNumber: number | undefined,
  selectedScoreGroups: number[],
  scoreGroupType: ScoreGroupType,
): ScoresOfWellDrops[] | null {
  const selectedInspection = getSelectedInspection(inspections, inspectionId);
  if (!selectedInspection) {
    return null;
  }

  const definitions = getScoreDefinitionsById();
  const scores = getAllPlateScores(plateDto, selectedInspection, dropNumber);

  return filterScoresBySelectedGroups(
    scores,
    scoreGroupType,
    selectedScoreGroups,
    definitions,
  );
}

function selectHitWells(
  groupedScores: ScoresOfWellDrops[],
  scoreGroupType: ScoreGroupType,
  requireAllHits: boolean,
  uvHitsOnly: boolean,
  definitions = getScoreDefinitionsById(),
): FilterWell[] {
  const wellStats = new Map<
    string,
    { total: number; hits: number; hasUvPositive: boolean; well: FilterWell }
  >();

  for (const score of groupedScores) {
    if (!isScoreInScope(score, scoreGroupType)) {
      continue;
    }

    const well: FilterWell = {
      plateId: score.plateId ?? 0,
      wellNumber: score.wellNumber,
      hit: true,
    };
    const wellKey = getWellKey(well);
    const stats = wellStats.get(wellKey) ?? {
      total: 0,
      hits: 0,
      hasUvPositive: false,
      well,
    };

    stats.total += 1;
    if (isUvPositive(score)) {
      stats.hasUvPositive = true;
    }
    if (isPositiveHitScore(score, definitions, scoreGroupType)) {
      stats.hits += 1;
    }

    wellStats.set(wellKey, stats);
  }

  return Array.from(wellStats.values())
    .filter((stats) => {
      if (requireAllHits) {
        const allHitsMatch = stats.total > 0 && stats.total === stats.hits;
        return allHitsMatch && (!uvHitsOnly || stats.hasUvPositive);
      }

      return uvHitsOnly ? stats.hasUvPositive : stats.hits > 0;
    })
    .map((stats) => stats.well);
}

export function scoresMatchHitsFilter(
  scores: ScoresOfWellDrops[] | undefined | null,
  selectedScoreGroups: number[] | null,
  scoreGroupType: ScoreGroupType = ScoreGroupType.All,
  requireAllHits = false,
  uvHitsOnly = false,
): boolean {
  if (!scores?.length) {
    return false;
  }

  const groupedScores =
    selectedScoreGroups === null
      ? scores
      : filterScoresBySelectedGroups(scores, scoreGroupType, selectedScoreGroups);

  if (!groupedScores.length) {
    return false;
  }

  const normalizedScores = groupedScores.map((score) => ({
    ...score,
    plateId: 0,
    wellNumber: 0,
  }));

  return (
    selectHitWells(normalizedScores, scoreGroupType, requireAllHits, uvHitsOnly).length >
    0
  );
}

function computeHitWells(
  inspections: any[] | undefined,
  plateDto: Plate[] | undefined,
  inspectionId: number | undefined,
  dropNumber: number | undefined,
  selectedScoreGroups: number[],
  scoreGroupType: ScoreGroupType,
  requireAllHits: boolean,
  uvHitsOnly: boolean,
): FilterWell[] {
  const groupedScores = getGroupedScores(
    inspections,
    plateDto,
    inspectionId,
    dropNumber,
    selectedScoreGroups,
    scoreGroupType,
  );

  if (!groupedScores) {
    return [];
  }

  return selectHitWells(groupedScores, scoreGroupType, requireAllHits, uvHitsOnly);
}

export function computeAnyHitWells(
  inspections: any[] | undefined,
  plateDto: Plate[] | undefined,
  inspectionId: number | undefined,
  dropNumber: number | undefined,
  selectedScoreGroups: number[],
  scoreGroupType: ScoreGroupType = ScoreGroupType.All,
  uvHitsOnly = false,
): FilterWell[] {
  return computeHitWells(
    inspections,
    plateDto,
    inspectionId,
    dropNumber,
    selectedScoreGroups,
    scoreGroupType,
    false,
    uvHitsOnly,
  );
}

export function computeAllHitWells(
  inspections: any[] | undefined,
  plateDto: Plate[] | undefined,
  inspectionId: number | undefined,
  dropNumber: number | undefined,
  selectedScoreGroups: number[],
  scoreGroupType: ScoreGroupType = ScoreGroupType.All,
  uvHitsOnly = false,
): FilterWell[] {
  return computeHitWells(
    inspections,
    plateDto,
    inspectionId,
    dropNumber,
    selectedScoreGroups,
    scoreGroupType,
    true,
    uvHitsOnly,
  );
}
